// Bubble Pop - pure logic: hex grid, deterministic generation, cluster
// matching, floating detection. No DOM imports.
//
// The board is a "odd-r offset" hex packing: even rows hold COLS bubbles, odd
// rows COLS-1 (shifted half a bubble to the right). A shot bubble snaps into
// the nearest empty cell; a same-color cluster of MATCH_MIN or more pops, and
// any bubbles left dangling (not connected to the ceiling) fall.
//
// Always winnable: there is no shot limit, and the next-bubble color is always
// drawn from colors still on the board - so even a lone stray bubble can be
// popped by shooting two of its own color next to it. Losing happens only when
// sloppy stacking reaches the deadline row.

export const COLS = 8;
export const MATCH_MIN = 3; // cluster size at which bubbles pop
export const MAX_ROWS = 11; // grid capacity; reaching DEADLINE_ROW loses
export const DEADLINE_ROW = 9; // 0-based: a bubble snapped at/past this row = lose

export const EMPTY = -1;

/** grid[r][c] = color index (0-based) or EMPTY. Row r has rowWidth(r) cells. */
export type Grid = number[][];

export interface BubbleLevel {
  n: number;
  colors: number; // number of distinct colors in play (color indices 0..colors-1)
  rows: number; // initially filled rows
  grid: Grid; // starting board (MAX_ROWS rows, unfilled cells EMPTY)
  par: number; // target number of shots for 3 stars
}

export function rowWidth(r: number): number {
  return r % 2 === 0 ? COLS : COLS - 1;
}

/** Center of cell (r,c) in bubble-diameter units (x right, y down). */
export function cellCenter(r: number, c: number): { x: number; y: number } {
  return { x: c + (r % 2) * 0.5 + 0.5, y: r * 0.866 + 0.5 };
}

export function inGrid(r: number, c: number): boolean {
  return r >= 0 && r < MAX_ROWS && c >= 0 && c < rowWidth(r);
}

/** The (up to 6) hex neighbors of a cell. */
export function neighbors(r: number, c: number): Array<[number, number]> {
  const even = r % 2 === 0;
  const cand: Array<[number, number]> = even
    ? [
        [r, c - 1],
        [r, c + 1],
        [r - 1, c - 1],
        [r - 1, c],
        [r + 1, c - 1],
        [r + 1, c],
      ]
    : [
        [r, c - 1],
        [r, c + 1],
        [r - 1, c],
        [r - 1, c + 1],
        [r + 1, c],
        [r + 1, c + 1],
      ];
  return cand.filter(([rr, cc]) => inGrid(rr, cc));
}

/** Same-color cluster containing (r,c) (including it). Empty cell -> []. */
export function cluster(grid: Grid, r: number, c: number): Array<[number, number]> {
  const color = grid[r]?.[c];
  if (color == null || color === EMPTY) return [];
  const seen = new Set<string>([`${r},${c}`]);
  const out: Array<[number, number]> = [[r, c]];
  const stack: Array<[number, number]> = [[r, c]];
  while (stack.length) {
    const [cr, cc] = stack.pop()!;
    for (const [nr, nc] of neighbors(cr, cc)) {
      const key = `${nr},${nc}`;
      if (!seen.has(key) && grid[nr][nc] === color) {
        seen.add(key);
        out.push([nr, nc]);
        stack.push([nr, nc]);
      }
    }
  }
  return out;
}

/** Occupied cells NOT connected (via any occupied path) to the ceiling row. */
export function floatingCells(grid: Grid): Array<[number, number]> {
  const seen = new Set<string>();
  const stack: Array<[number, number]> = [];
  for (let c = 0; c < rowWidth(0); c++) {
    if (grid[0][c] !== EMPTY) {
      seen.add(`0,${c}`);
      stack.push([0, c]);
    }
  }
  while (stack.length) {
    const [cr, cc] = stack.pop()!;
    for (const [nr, nc] of neighbors(cr, cc)) {
      const key = `${nr},${nc}`;
      if (!seen.has(key) && grid[nr][nc] !== EMPTY) {
        seen.add(key);
        stack.push([nr, nc]);
      }
    }
  }
  const out: Array<[number, number]> = [];
  for (let r = 0; r < MAX_ROWS; r++) {
    for (let c = 0; c < rowWidth(r); c++) {
      if (grid[r][c] !== EMPTY && !seen.has(`${r},${c}`)) out.push([r, c]);
    }
  }
  return out;
}

export function countBubbles(grid: Grid): number {
  let n = 0;
  for (const row of grid) for (const v of row) if (v !== EMPTY) n++;
  return n;
}

/** Distinct colors still on the board. */
export function colorsInGrid(grid: Grid): number[] {
  const set = new Set<number>();
  for (const row of grid) for (const v of row) if (v !== EMPTY) set.add(v);
  return [...set].sort((a, b) => a - b);
}

export function cloneGrid(grid: Grid): Grid {
  return grid.map((row) => row.slice());
}

// ---- Deterministic RNG (mulberry32, same scheme as the other games) --------

export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Seed for the shooter queue - separate stream so a restart replays the same queue. */
export function queueSeed(n: number): number {
  return (9000 + n * 2654435761) >>> 0;
}

/**
 * Next bubble color: drawn only from colors still on the board, so the player
 * never receives a useless color (this is also what keeps every level
 * finishable down to the last bubble).
 */
export function nextColor(rng: () => number, grid: Grid): number {
  const present = colorsInGrid(grid);
  if (present.length === 0) return 0;
  return present[Math.floor(rng() * present.length)];
}

// ---- Generation -------------------------------------------------------------

/** Difficulty curve: more rows and more colors as n grows. */
export function shapeForBubbleLevel(n: number): { rows: number; colors: number } {
  const rows = Math.min(7, 3 + Math.floor(n / 6));
  const colors = Math.min(6, 3 + Math.floor(n / 8));
  return { rows, colors };
}

/** Generate one level deterministically from its number. */
export function generateBubbleLevel(n: number): BubbleLevel {
  const { rows, colors } = shapeForBubbleLevel(n);
  const rng = makeRng((7000 + n * 2654435761) >>> 0);

  const grid: Grid = [];
  for (let r = 0; r < MAX_ROWS; r++) {
    grid.push(new Array<number>(rowWidth(r)).fill(EMPTY));
  }
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < rowWidth(r); c++) {
      grid[r][c] = Math.floor(rng() * colors);
    }
  }
  // Guarantee every color is actually present (a missing color would make the
  // difficulty curve lie about itself). Only overwrite cells whose color has
  // spares, so fixing one color can never make another one go missing.
  for (let guard = 0; guard < 64; guard++) {
    const counts = new Array<number>(colors).fill(0);
    for (let r = 0; r < rows; r++) for (const v of grid[r]) if (v !== EMPTY) counts[v]++;
    const missing = counts.findIndex((k) => k === 0);
    if (missing < 0) break;
    const r = Math.floor(rng() * rows);
    const c = Math.floor(rng() * rowWidth(r));
    if (counts[grid[r][c]] >= 2) grid[r][c] = missing;
  }

  // Par: a good player pops ~3 per shot, plus a little slack per extra color
  // for setup shots. Stars: <=par 3, <=1.5*par 2, else 1.
  const par = Math.ceil(countBubbles(grid) / 3) + colors;

  return { n, colors, rows, grid, par };
}
